import type { ClimbSessionOut } from "@/types/api";
import * as api from "@/api/sessions";
import { getCurrentUserId } from "./currentUser";
import {
  deletePendingRoute,
  deletePendingSession,
  getPendingSession,
  isLocalId,
  listPendingRoutesForSession,
  listPendingSessions,
  nextLocalId,
  putPendingSession,
  revokeObjectUrl,
} from "./db";
import { OfflineEditBlockedError } from "./errors";
import { isNetworkError, isOnline } from "./network";
import { clearRouteSyncStatus, listPendingRoutesAsOutForSession } from "./routesRepo";
import { clearSessionSyncStatus, getSessionSyncStatus, setSessionSyncStatus } from "./statusCache";
import { refreshPendingCounts } from "./syncEngine";
import type { PendingSession, SessionData } from "./types";
import { withoutNulls } from "./utils";

export { getSessionSyncStatus, clearSessionSyncStatus };

function toClimbSessionOut(pending: PendingSession, routes: ClimbSessionOut["routes"]): ClimbSessionOut {
  setSessionSyncStatus(pending.id, pending.status);
  return {
    id: pending.id,
    user_id: pending.createdByUserId,
    session_date: pending.data.session_date,
    gym_name: pending.data.gym_name ?? null,
    session_type: pending.data.session_type ?? null,
    area: pending.data.area ?? null,
    crag_name: pending.data.crag_name ?? null,
    notes: pending.data.notes ?? null,
    challenge_year: null,
    created_at: pending.createdAt,
    routes,
  };
}

async function listPendingSessionsForCurrentUser(): Promise<PendingSession[]> {
  const userId = getCurrentUserId();
  const all = await listPendingSessions();
  return all.filter((s) => s.createdByUserId === userId);
}

export async function createSession(data: SessionData): Promise<ClimbSessionOut> {
  if (await isOnline()) {
    try {
      return await api.createSession(withoutNulls(data));
    } catch (err) {
      if (!isNetworkError(err)) throw err;
    }
  }

  const userId = getCurrentUserId();
  if (userId == null) throw new Error("Cannot create a session while signed out.");

  const id = await nextLocalId();
  const pending: PendingSession = {
    id,
    createdByUserId: userId,
    status: "pending",
    createdAt: new Date().toISOString(),
    data,
  };
  await putPendingSession(pending);
  void refreshPendingCounts();
  return toClimbSessionOut(pending, []);
}

export async function getSession(id: number): Promise<ClimbSessionOut> {
  if (isLocalId(id)) {
    const pending = await getPendingSession(id);
    if (!pending) throw new Error("Session not found.");
    const routes = await listPendingRoutesAsOutForSession(id);
    return toClimbSessionOut(pending, routes);
  }

  const session = await api.getSession(id);
  const pendingRoutes = await listPendingRoutesAsOutForSession(id);
  return pendingRoutes.length ? { ...session, routes: [...session.routes, ...pendingRoutes] } : session;
}

type ListSessionsParams = {
  year?: number;
  since?: string;
  until?: string;
};

export async function listSessions(params?: ListSessionsParams): Promise<ClimbSessionOut[]> {
  return (await listSessionsWithStatus(params)).sessions;
}

/**
 * Like `listSessions`, but also says whether the synced sessions are missing.
 *
 * Offline, the server's list can't be fetched, so `sessions` holds only what
 * was logged locally and `syncedError` holds the network error. Callers that
 * show the whole logbook use it to say so instead of looking empty.
 */
export async function listSessionsWithStatus(
  params?: ListSessionsParams
): Promise<{ sessions: ClimbSessionOut[]; syncedError: unknown }> {
  let syncedError: unknown = null;
  const [real, pendingSessions] = await Promise.all([
    api.listSessions(params).catch((err: unknown) => {
      if (!isNetworkError(err)) throw err;
      syncedError = err;
      return [] as ClimbSessionOut[];
    }),
    listPendingSessionsForCurrentUser(),
  ]);

  const pendingAsOut = await Promise.all(
    pendingSessions.map(async (p) => toClimbSessionOut(p, await listPendingRoutesAsOutForSession(p.id)))
  );

  // A route logged offline against an already-synced session should show up
  // before it syncs too, not just brand-new offline sessions.
  const merged = await Promise.all(
    real.map(async (s) => {
      const extra = await listPendingRoutesAsOutForSession(s.id);
      return extra.length ? { ...s, routes: [...s.routes, ...extra] } : s;
    })
  );

  const sessions = [...pendingAsOut, ...merged].sort((a, b) => {
    if (a.session_date !== b.session_date) return a.session_date < b.session_date ? 1 : -1;
    return b.id - a.id;
  });
  return { sessions, syncedError };
}

export async function updateSession(id: number, data: Partial<SessionData>): Promise<ClimbSessionOut> {
  if (isLocalId(id)) {
    const pending = await getPendingSession(id);
    if (!pending) throw new Error("Session not found.");
    const updated: PendingSession = {
      ...pending,
      data: { ...pending.data, ...data },
      status: "pending",
      error: undefined,
    };
    await putPendingSession(updated);
    void refreshPendingCounts();
    const routes = await listPendingRoutesAsOutForSession(id);
    return toClimbSessionOut(updated, routes);
  }
  if (!(await isOnline())) throw new OfflineEditBlockedError();
  return api.updateSession(id, data);
}

export async function deleteSession(id: number): Promise<void> {
  if (isLocalId(id)) {
    const routes = await listPendingRoutesForSession(id);
    for (const route of routes) {
      revokeObjectUrl(route.id);
      clearRouteSyncStatus(route.id);
      await deletePendingRoute(route.id);
    }
    clearSessionSyncStatus(id);
    await deletePendingSession(id);
    void refreshPendingCounts();
    return;
  }
  if (!(await isOnline())) throw new OfflineEditBlockedError();
  await api.deleteSession(id);
}
